import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import {
  getDownloadURL,
  getMetadata,
  listAll,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { FaDownload, FaFileAlt, FaFolderOpen, FaUpload } from 'react-icons/fa';

import { auth, storage } from '../../firebase/firebase';

const crearIdDocumento = () => (
  window.crypto?.randomUUID?.()
  || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

const obtenerExtension = (nombre = '') => {
  const coincidencia = nombre.match(/(\.[a-zA-Z0-9]{1,12})$/);
  return coincidencia ? coincidencia[1].toLowerCase() : '';
};

const formatearTamano = (bytes = 0) => {
  if (!bytes) return 'Tamaño no disponible';
  const unidades = ['B', 'KB', 'MB', 'GB'];
  const indice = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), unidades.length - 1);
  const valor = bytes / (1024 ** indice);
  return `${valor >= 10 || indice === 0 ? valor.toFixed(0) : valor.toFixed(1)} ${unidades[indice]}`;
};

const formatearFecha = (valor) => {
  const fecha = valor ? new Date(valor) : null;
  if (!fecha || Number.isNaN(fecha.getTime())) return 'Fecha no disponible';
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(fecha);
};

const DocumentosHistoricosAlumno = ({ alumnoId }) => {
  const inputRef = useRef(null);
  const [documentos, setDocumentos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');

  const cargarDocumentos = useCallback(async () => {
    if (!alumnoId || !storage) {
      setError('No fue posible acceder al archivo histórico del alumno.');
      setCargando(false);
      return;
    }

    setCargando(true);
    setError('');

    try {
      const carpeta = storageRef(storage, `fichas-alumnos/${alumnoId}/documentos`);
      const resultado = await listAll(carpeta);
      const items = await Promise.all(resultado.items.map(async (referencia) => {
        const [metadata, url] = await Promise.all([
          getMetadata(referencia),
          getDownloadURL(referencia),
        ]);
        return {
          id: referencia.fullPath,
          nombre: metadata.customMetadata?.nombreOriginal || referencia.name,
          creadoEn: metadata.timeCreated,
          tamano: metadata.size,
          tipo: metadata.contentType || 'Archivo',
          url,
        };
      }));

      setDocumentos(items.sort((a, b) => new Date(b.creadoEn) - new Date(a.creadoEn)));
    } catch (loadError) {
      console.error('Error cargando documentos históricos:', loadError);
      setError('No se pudieron cargar los documentos históricos. Verifica tu conexión e inténtalo nuevamente.');
    } finally {
      setCargando(false);
    }
  }, [alumnoId]);

  useEffect(() => {
    void cargarDocumentos();
  }, [cargarDocumentos]);

  const subirDocumento = async (event) => {
    const archivo = event.target.files?.[0];
    event.target.value = '';
    if (!archivo || !alumnoId || !storage || subiendo) return;

    setSubiendo(true);
    setError('');

    try {
      const referencia = storageRef(
        storage,
        `fichas-alumnos/${alumnoId}/documentos/${crearIdDocumento()}${obtenerExtension(archivo.name)}`,
      );
      await uploadBytes(referencia, archivo, {
        contentType: archivo.type || 'application/octet-stream',
        contentDisposition: 'attachment',
        customMetadata: {
          nombreOriginal: archivo.name,
          cargadoPor: auth?.currentUser?.uid || 'usuario-autenticado',
        },
      });
      await cargarDocumentos();
    } catch (uploadError) {
      console.error('Error subiendo documento histórico:', uploadError);
      setError('El documento no pudo cargarse. Revisa los permisos de almacenamiento e inténtalo nuevamente.');
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <section className="documentos-historicos" aria-labelledby="documentos-historicos-title">
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        onChange={subirDocumento}
        aria-label="Seleccionar documento para la ficha histórica"
      />

      <div className="documentos-historicos__heading">
        <div>
          <span>Archivo de la ficha</span>
          <h3 id="documentos-historicos-title">Documentos históricos</h3>
          <p>Los archivos se conservan como respaldo y permanecen disponibles para descarga.</p>
        </div>
        <Button
          type="button"
          variant="primary"
          disabled={subiendo}
          onClick={() => inputRef.current?.click()}
        >
          {subiendo ? <Spinner size="sm" animation="border" aria-hidden="true" /> : <FaUpload aria-hidden="true" />}
          {subiendo ? 'Cargando…' : 'Cargar documento'}
        </Button>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {cargando ? (
        <div className="documentos-historicos__state">
          <Spinner animation="border" size="sm" />
          <span>Cargando documentos…</span>
        </div>
      ) : documentos.length === 0 ? (
        <div className="documentos-historicos__state documentos-historicos__state--empty">
          <FaFolderOpen aria-hidden="true" />
          <div>
            <strong>Aún no hay documentos</strong>
            <span>Carga el primer archivo para comenzar el historial documental.</span>
          </div>
        </div>
      ) : (
        <ul className="documentos-historicos__list">
          {documentos.map((documento) => (
            <li key={documento.id}>
              <span className="documentos-historicos__icon" aria-hidden="true"><FaFileAlt /></span>
              <div className="documentos-historicos__info">
                <strong title={documento.nombre}>{documento.nombre}</strong>
                <span>{formatearFecha(documento.creadoEn)} · {formatearTamano(documento.tamano)}</span>
              </div>
              <a className="btn btn-outline-primary" href={documento.url} download={documento.nombre}>
                <FaDownload aria-hidden="true" />
                Descargar
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default DocumentosHistoricosAlumno;
